import { ActionIcon, Center, Container, Group, Loader, Tabs, Text, Title, Tooltip } from '@mantine/core'
import { useState } from 'react'
import { IconCake, IconClipboardList, IconLayoutKanban, IconListDetails, IconUserCircle, IconUsers } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { IconPencil } from '@tabler/icons-react'
import { fetchProject } from '../shared/api/projects'
import { AppLayout } from '../features/projects/app-shell'
import { OverviewTab } from '../features/project-details/overview-tab'
import { LifecycleTab } from '../features/project-details/lifecycle-tab'
import { ContactsTab } from '../features/project-details/contacts-tab'
import { BirthdaysTab } from '../features/project-details/birthdays-tab'
import { MembersTab } from '../features/project-details/members-tab'
import { RenameModal } from '../features/projects/project-dialogs'
import { useAuthStore } from '../features/auth/store'

/**
 * Epic B-S3: страница деталей проекта с табами. Структура:
 *  - Обзор (CRM-поля: бюджет, lead-source, NPS, заметки, дедлайн follow-up)
 *  - Лайфцикл (воронка стадий с прогресс-баром и журналом)
 *  - Контакты (CRUD клиентских контактов)
 *  - ДР (ближайшие дни рождения этого проекта)
 *  - Доска (ссылка на /projects/:id — главный канбан)
 */
export function ProjectDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id!),
    enabled: !!id,
  })
  const me = useAuthStore((st) => st.user)
  // Зеркало серверного assertCanModify (как canEditLead в overview-tab): projects.edit И
  // admin/director | head отдела проекта | account-лид — иначе PATCH вернёт 403.
  const canRename =
    !!data &&
    !!me?.permissions.includes('projects.edit') &&
    (me.role === 'admin' ||
      me.role === 'director' ||
      (me.role === 'head' && me.departments.some((d) => data.departments.some((pd) => pd.id === d.id))) ||
      (me.role === 'account' && data.lead.id === me.id))
  const [renaming, setRenaming] = useState(false)

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <div>
            <Text size="xs" c="blue.7" fw={700} tt="uppercase">
              Детали проекта
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Title order={2}>{data?.name ?? '…'}</Title>
              {canRename && (
                <Tooltip label="Переименовать">
                  <ActionIcon variant="subtle" color="gray" aria-label="Переименовать проект" onClick={() => setRenaming(true)}>
                    <IconPencil size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </div>
          <Group>
            <Text component={Link} to={`/projects/${id}`} c="blue" td="underline">
              <IconLayoutKanban size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              К доске
            </Text>
          </Group>
        </Group>

        {isLoading && (
          <Center mih={300}>
            <Loader />
          </Center>
        )}

        {data && (
          <Tabs defaultValue="overview" keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="overview" leftSection={<IconClipboardList size={16} />}>Обзор</Tabs.Tab>
              <Tabs.Tab value="lifecycle" leftSection={<IconListDetails size={16} />}>Лайфцикл</Tabs.Tab>
              <Tabs.Tab value="contacts" leftSection={<IconUsers size={16} />}>Контакты</Tabs.Tab>
              <Tabs.Tab value="birthdays" leftSection={<IconCake size={16} />}>Дни рождения</Tabs.Tab>
              <Tabs.Tab value="members" leftSection={<IconUserCircle size={16} />}>Участники</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview">
              <OverviewTab project={data} />
            </Tabs.Panel>
            <Tabs.Panel value="lifecycle">
              <LifecycleTab projectId={data.id} />
            </Tabs.Panel>
            <Tabs.Panel value="contacts">
              <ContactsTab projectId={data.id} />
            </Tabs.Panel>
            <Tabs.Panel value="birthdays">
              <BirthdaysTab projectId={data.id} />
            </Tabs.Panel>
            <Tabs.Panel value="members">
              <MembersTab project={data} />
            </Tabs.Panel>
          </Tabs>
        )}
      </Container>
      <RenameModal
        target={renaming && data ? { kind: 'project', id: data.id, name: data.name } : null}
        onClose={() => setRenaming(false)}
      />
    </AppLayout>
  )
}
